import React from 'react';
import { useNavigate } from 'react-router-dom';
import { InspectorModel, Variant, TextField, ColorField, ImageField, MetadataField } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { toast } from 'utils/toast';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { getSectionIdFromElementKey, SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { ROUTE_TREE } from 'routes/AppRoutes';
import PalettePickerDialog from '../PalettePickerDialog';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField as MuiTextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Info as InfoIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Description as FileTextIcon,
  Image as ImageIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

type Props = {
  templateKey: string;
  elementKey: string;
  variant: Variant;
  onValuesChange?: (values: Record<string, any>) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  onSaveReady?: (saveFn: () => Promise<void>) => void; // Expose save function to parent
  onModifiedCountChange?: (count: number) => void; // Expose modified count to parent
  onCancelReady?: (cancelFn: () => void) => void; // Expose cancel/reset function to parent
  renderedHtml?: string; // Rendered HTML from PreviewArea to get technical section IDs
  onSaveSuccess?: () => void; // Callback when save succeeds to trigger preview refresh
  refreshKey?: number | null; // Refresh trigger to force inspector API reload after save
}

// Contrast checking functions
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function getLuminance(r: number, g: number, b: number): number {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrastRatio(hex1: string, hex2: string): number | null {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

export const InspectorPanel: React.FC<Props> = ({ 
  templateKey, 
  elementKey, 
  variant, 
  onValuesChange,
  refreshKey,
  isOpen = true,
  onToggleOpen,
  onSaveReady,
  onModifiedCountChange,
  onCancelReady,
  renderedHtml = '',
  onSaveSuccess
}) => {
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  const [data, setData] = React.useState<InspectorModel>({ text: [], colors: [], images: [], metadata: [] });
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'text' | 'colors' | 'images' | 'metadata'>('text');
  const isInitialLoadRef = React.useRef(true); // Track if this is the initial load to preserve activeTab after save
  
  // Get technical section ID from HTML (e.g., "HEADER_V4", "GREETING_V2") instead of mapped name
  const getTechnicalSectionName = React.useCallback((elementKey: string, html: string): string => {
    if (elementKey === 'Full Template') {
      return 'FULL_TEMPLATE'; // API expects "FULL_TEMPLATE" for full template
    }
    
    // Get the actual technical section ID from HTML (e.g., "HEADER_V4", "GREETING_V2")
    const technicalSectionId = getSectionIdFromElementKey(elementKey, html);
    
    if (technicalSectionId) {
      console.log(`[InspectorPanel] Using technical section ID: "${technicalSectionId}" for elementKey: "${elementKey}"`);
      return technicalSectionId;
    }
    
    // Fallback: if HTML not available or section not found, use elementKey with spaces removed
    console.warn(`[InspectorPanel] Could not find technical section ID for "${elementKey}" in HTML, using fallback`);
    return elementKey.replace(/\s+/g, '');
  }, []);

  // Get display name from elementKey (converts technical section ID to display name)
  // elementKey can be: "Full Template", technical section ID (e.g., "MONTHLY_SUMMARY_V2"), or display name (e.g., "Bill Summary")
  const getDisplayName = React.useCallback((elementKey: string): string => {
    if (elementKey === 'Full Template') {
      return 'Full Template';
    }
    
    // Check if elementKey is a technical section ID (exists as key in mapping)
    // If yes, return the mapped display name
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey];
    }
    
    // If elementKey is already a display name (not in mapping), return it as-is
    return elementKey;
  }, []);
  const valuesRef = React.useRef<Record<string, any>>({});
  const originalValuesRef = React.useRef<Record<string, any>>({}); // Track original values for change detection
  const textFieldKeysRef = React.useRef<Set<string>>(new Set()); // Track all text field keys (both configs and stringResources)
  const configDataRef = React.useRef<Array<{
    configKey: string;
    configType: string;
    configLevel: string;
    configDataType: string;
    displayName: string;
    documentation: string;
    configRegex: string;
    component: string;
  }>>([]); // Store config metadata for save (text fields)
  const stringResourceDataRef = React.useRef<Array<{
    stringResourceId: string;
    locale: string;
    stringLevel: string;
    displayName: string;
    documentation: string;
  }>>([]); // Store stringResource metadata for save (text fields)
  const imageConfigDataRef = React.useRef<Array<{
    configKey: string;
    configType: string;
    configLevel: string;
    configDataType: string;
    displayName: string;
    documentation: string;
    configRegex: string;
    component: string;
  }>>([]); // Store image config metadata for save
  const colorConfigDataRef = React.useRef<Array<{
    configKey: string;
    configType: string;
    configLevel: string;
    configDataType: string;
    displayName: string;
    documentation: string;
    configRegex: string;
    component: string;
  }>>([]); // Store color config metadata for save
  const metadataConfigDataRef = React.useRef<Array<{
    configKey: string;
    configType: string;
    configLevel: string;
    configDataType: string;
    displayName: string;
    documentation: string;
    configRegex: string;
    component: string;
    possibleValues?: any[];
  }>>([]); // Store metadata config metadata for save
  // Store color groups for linked editing - maps uniqueKey to array of configKeys
  const colorGroupsRef = React.useRef<Map<string, string[]>>(new Map());
  const [paletteOpen, setPaletteOpen] = React.useState<{ open: boolean; colorId?: string }>({ open: false });
  const [undoSignOffOpen, setUndoSignOffOpen] = React.useState(false);
  const [overrideAlertOpen, setOverrideAlertOpen] = React.useState<{ open: boolean; colorId?: string; action?: 'create' | 'revert' }>({ open: false });
  const textareaRefs = React.useRef<Record<string, HTMLTextAreaElement>>({});

  const isFullTemplate = elementKey === 'Full Template';
  const isSignedOff = data.signedOff === true;

  // Calculate contrast background color
  const contrastBackgroundColor = React.useMemo(() => {
    const bg = data.colors.find(c => c.label === 'Background 500');
    return bg ? bg.value : '#FFFFFF';
  }, [data.colors]);

  // Parse templateKey to extract productType and notificationType
  const parseTemplateKey = (templateKey: string) => {
    const parts = templateKey.split('.');
    let productType = 'EMAIL'; // API expects uppercase
    let notificationType = '';
    
    if (templateKey.startsWith('EMAIL')) {
      productType = 'EMAIL'; // Uppercase as per API requirement
      // Extract notification type (e.g., "EMAIL.SF.MONTHLY_SUMMARY" -> "MONTHLY_SUMMARY")
      notificationType = parts[parts.length - 1] || '';
    } else if (templateKey.startsWith('HER')) {
      productType = 'HER';
      notificationType = 'HER';
    } else if (templateKey.startsWith('WEB') || templateKey.startsWith('DASHBOARD')) {
      productType = 'WEB';
      notificationType = parts[parts.length - 1] || '';
    } else if (templateKey.startsWith('SMS')) {
      productType = 'SMS';
      notificationType = parts[parts.length - 1] || '';
    }
    
    return { productType, notificationType };
  };

  // Map elementKey to sectionName
  const mapElementKeyToSectionName = (elementKey: string): string => {
    if (elementKey === 'Full Template') return 'FULL_TEMPLATE'; // API expects "FULL_TEMPLATE" not empty
    // Convert to camelCase or keep as is, based on API requirement
    // For other elements, convert spaces and use appropriate casing
    return elementKey.replace(/\s+/g, ''); // Remove spaces: "Header" -> "Header", "Bill Summary" -> "BillSummary"
  };

  React.useEffect(() => {
    if (!templateKey) return;
    let ignore = false;
    async function load() {
      setLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Use selectedPilotId from Redux
        const pilotId = selectedPilotId;
        
        if (!pilotId) {
          toast.error('No pilot selected. Please select a pilot from the sidebar.');
          setLoading(false);
          return;
        }
        
        const { productType, notificationType } = parseTemplateKey(templateKey);
        
        // Get fuel_type from Redux, defaulting to 'ELECTRIC' if not available
        let fuelType = 'ELECTRIC'; // Default fallback
        if (projectAttributes?.fuel_type) {
          // Handle different formats: string, array, or comma-separated
          if (typeof projectAttributes.fuel_type === 'string') {
            // If comma-separated, take the first one
            const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
            fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
          } else if (Array.isArray(projectAttributes.fuel_type)) {
            const fuelTypeArray = projectAttributes.fuel_type as any[];
            if (fuelTypeArray.length > 0) {
              fuelType = String(fuelTypeArray[0]).toUpperCase();
            }
          }
        }
        
        // Get technical section name from HTML (e.g., "HEADER_V4" instead of "Header")
        let sectionName = getTechnicalSectionName(elementKey, renderedHtml);
        
        // For Web templates, use WEB_DASHBOARD as notificationType in entityId
        // For other channels, use the extracted notificationType
        const entityIdNotificationType = productType === 'WEB' ? 'WEB_DASHBOARD' : notificationType;
        const entityId = `${pilotId}.${entityIdNotificationType}.${fuelType}`;
        
        // Validate required parameters
        if (!notificationType) {
          console.error('[InspectorPanel] notificationType is empty for templateKey:', templateKey);
          throw new Error(`Unable to extract notificationType from templateKey: ${templateKey}`);
        }
        
        // Ensure sectionName is not empty (API requires a value)
        if (!sectionName || sectionName === '') {
          console.warn('[InspectorPanel] sectionName is empty, defaulting to FullTemplate');
          sectionName = 'FullTemplate'; // Default to FullTemplate if empty
        }
        
        // Key format: ${projectId}_DIGITAL (default channel is DIGITAL)
        let backgroundHex = '#FFFFFF';
        try {
          const colorPaletteSettingsStr = sessionStorage.getItem('colorPaletteSettings');
          if (colorPaletteSettingsStr) {
            const colorPaletteSettings = JSON.parse(colorPaletteSettingsStr);
            const settingsKey = `${pilotId}_DIGITAL`;
            const entry = colorPaletteSettings[settingsKey];
            
            if (entry?.backgroundHEX && entry?.expiryTs) {
              const isExpired = Date.now() > entry.expiryTs;
              if (!isExpired) {
                backgroundHex = entry.backgroundHEX;
              }
            }
          }
        } catch (e) {
          console.warn('[InspectorPanel] Failed to parse colorPaletteSettings from sessionStorage:', e);
        }
        
        const params: any = {
          entityType: 'pilot',
          entityId: String(entityId),
          pilot_id: String(pilotId),
          productType: String(productType),
          notificationType: String(notificationType),
          sectionName: String(sectionName), 
          locale: String(variant.locale || 'en_US'),
          backgroundHex: backgroundHex 
        };
        
        // For web channel, add templateKey as string with full template key name
        if (productType === 'WEB') {
          params.templateKey = templateKey;
        }
        
        console.log('[InspectorPanel] Calling getInspectorConfigs with params:', params);
        console.log('[InspectorPanel] API URL:', process.env.REACT_APP_TOOL_API_URL);
        console.log('[InspectorPanel] Parsed values:', { templateKey, elementKey, productType, notificationType, sectionName, entityId });
        
        // Verify all params are present
        const requiredParams = ['entityType', 'entityId', 'pilot_id', 'productType', 'notificationType', 'sectionName', 'locale'];
        const missingParams = requiredParams.filter(key => !params[key as keyof typeof params] && params[key as keyof typeof params] !== '');
        if (missingParams.length > 0) {
          console.error('[InspectorPanel] Missing required parameters:', missingParams);
        }
        
        const resp = await api.getInspectorConfigs(params);
        
        console.log('[InspectorPanel] API response received:', resp);
        console.log('[InspectorPanel] Response structure check:', {
          hasResp: !!resp,
          respType: typeof resp,
          respKeys: resp ? Object.keys(resp) : [],
          hasConfigs: !!resp?.configs,
          configsType: typeof resp?.configs,
          configsKeys: resp?.configs ? Object.keys(resp.configs) : [],
          configsText: resp?.configs?.text,
          configsTextLength: resp?.configs?.text?.length,
          configsTextType: Array.isArray(resp?.configs?.text),
          hasStringResources: !!resp?.stringResources,
          stringResourcesKeys: resp?.stringResources ? Object.keys(resp.stringResources) : [],
          stringResourcesText: resp?.stringResources?.text,
          stringResourcesTextLength: resp?.stringResources?.text?.length,
          fullResponseString: JSON.stringify(resp, null, 2)
        });
        
        if (!ignore) {
          // Validate response structure
          if (!resp || typeof resp !== 'object') {
            throw new Error('Invalid API response: response is not an object');
          }
          
          // Handle potential nested response structure (e.g., resp.data.configs)
          let actualResp = resp;
          if (resp.data && typeof resp.data === 'object' && resp.data.configs) {
            console.warn('[InspectorPanel] Response nested in data property, unwrapping');
            actualResp = resp.data;
          }
          
          if (!actualResp.configs || typeof actualResp.configs !== 'object') {
            console.error('[InspectorPanel] Response missing configs object');
            console.error('[InspectorPanel] Full response:', JSON.stringify(resp, null, 2));
            console.error('[InspectorPanel] Actual response after unwrapping:', JSON.stringify(actualResp, null, 2));
            throw new Error('API response missing configs object');
          }
          
          // Transform response to InspectorModel format
          // Handle text fields from configs
          const configsText = actualResp.configs?.text || [];
          console.log('[InspectorPanel] configsText array:', configsText, 'Length:', configsText.length);
          const configsTextFields: TextField[] = configsText.map((item: any) => {
            if (!item.configKey) {
              console.warn('[InspectorPanel] Text item missing configKey:', item);
              return null;
            }
            // Backend returns configDocumentation, map it to documentation
            const documentation = item.documentation || item.configDocumentation || item.displayName;
            return {
              id: item.configKey,
              label: item.displayName || item.configKey,
              value: item.configValue || '',
              fieldName: item.configKey,
              description: documentation
            };
          }).filter((item: TextField | null) => item !== null) as TextField[];
          
          // Handle stringResources.text (localized strings)
          const stringResourcesText = actualResp.stringResources?.text || [];
          console.log('[InspectorPanel] stringResourcesText array:', stringResourcesText, 'Length:', stringResourcesText.length);
          const stringResourceFields: TextField[] = stringResourcesText.map((item: any) => {
            if (!item.stringResourceId) {
              console.warn('[InspectorPanel] StringResource item missing stringResourceId:', item);
              return null;
            }
            return {
              id: item.stringResourceId,
              label: item.displayName || item.stringResourceId,
              value: item.stringValue || '',
              fieldName: item.stringResourceId,
              description: item.documentation || item.displayName
            };
          }).filter((item: TextField | null) => item !== null) as TextField[];
          
          // Combine configs.text and stringResources.text
          // Configs first, then stringResources (maintains API order within each group)
          const transformedText: TextField[] = [...configsTextFields, ...stringResourceFields];
          console.log('[InspectorPanel] Transformed text fields:', {
            configsTextFieldsCount: configsTextFields.length,
            stringResourceFieldsCount: stringResourceFields.length,
            totalTransformedText: transformedText.length,
            sampleFields: transformedText.slice(0, 3).map(t => ({ id: t.id, label: t.label, value: t.value }))
          });
          
          // Store config metadata for save (only for configs.text, not stringResources)
          // StringResources use stringResourceId, not configKey, so they're handled differently
          configDataRef.current = (actualResp.configs?.text || [])
            .filter((item: any) => item.configKey) // Only include valid items
            .map((item: any) => {
              // Backend returns configDocumentation, use it if documentation is not available
              // Preserve documentation value as-is from API (including "Null" string if that's what API returns)
              const doc = item.documentation || item.configDocumentation;
              const normalizedDoc = (doc === null || doc === undefined) ? '' : String(doc).trim();
              return {
                configKey: item.configKey,
                configType: item.configType || 'EMAIL_CONFIG',
                configLevel: item.configLevel || 'pilot',
                configDataType: item.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
                displayName: item.displayName || item.configKey,
                documentation: normalizedDoc,
                configRegex: item.configRegex || '.*',
                component: item.component || 'Email'
              };
            });
          
          // Store stringResource metadata for save
          stringResourceDataRef.current = (actualResp.stringResources?.text || [])
            .filter((item: any) => item.stringResourceId) // Only include valid items
            .map((item: any) => ({
              stringResourceId: item.stringResourceId,
              locale: item.locale || variant.locale || 'en_US',
              stringLevel: item.stringLevel || 'pilot',
              displayName: item.displayName || item.stringResourceId,
              documentation: item.documentation || ''
            }));
          
          // Transform colors from configs with deduplication
          const configsColors = actualResp.configs?.colors || [];
          
          // Use Map to group duplicate colors by stdColorId + shade + hexValue
          const colorGroups = new Map<string, {
            color: any;         
            configKeys: string[]; 
          }>();

          configsColors.forEach((item: any) => {
            if (!item.configKey) {
              console.warn('[InspectorPanel] Color item missing configKey:', item);
              return;
            }
            
            // Skip entries where shade or stdColorDisplayName is empty/null/not found
            if (!item.shade || item.shade === '' || !item.stdColorDisplayName || item.stdColorDisplayName === '') {
              console.log('[InspectorPanel] Skipping color entry - missing shade or stdColorDisplayName:', item.configKey);
              return;
            }
            
            // Create unique key: stdColorId + shade + hexValue
            const uniqueKey = `${item.stdColorId || 'no-std'}_${item.shade}_${(item.hexValue || item.configValue || '#000000').toUpperCase()}`;
            
            if (colorGroups.has(uniqueKey)) {
              colorGroups.get(uniqueKey)!.configKeys.push(item.configKey);
              console.log(`[InspectorPanel] Grouped duplicate color: ${item.configKey} -> ${uniqueKey}`);
            } else {
              colorGroups.set(uniqueKey, {
                color: item,
                configKeys: [item.configKey]
              });
            }
          });

          colorGroupsRef.current = new Map(
            Array.from(colorGroups.entries()).map(([key, group]) => [key, group.configKeys])
          );

          const transformedColors: ColorField[] = Array.from(colorGroups.entries()).map(([uniqueKey, group]) => {
            const item = group.color;
            
            // Handle "Null" string as empty documentation
            const documentation = (item.configDocumentation === 'Null' || !item.configDocumentation) 
              ? '' 
              : item.configDocumentation;
            
            return {
              id: uniqueKey,  // Use uniqueKey as id (for React key and lookups)
              label: item.stdColorDisplayName,  
              value: item.hexValue,
              fieldName: uniqueKey, 
              description: documentation,
              token: item.standardId || item.stdColorId,
              isOverride: item.configValue !== item.defaultValue,
              contrastRatio: item.contrastRatio,
              contrastPass: item.contrastPass
            } as ColorField;
          });

          console.log('[InspectorPanel] Color deduplication:', {
            originalCount: configsColors.length,
            deduplicatedCount: transformedColors.length,
            groups: Array.from(colorGroups.entries()).map(([k, v]) => ({ key: k, configKeys: v.configKeys }))
          });
          
          // Transform images from configs
          const configsImages = actualResp.configs?.images || [];
          const transformedImages: ImageField[] = configsImages.map((item: any) => {
            if (!item.configKey) {
              console.warn('[InspectorPanel] Image item missing configKey:', item);
              return null;
            }
            // Backend returns configDocumentation, map it to documentation
            const documentation = item.documentation || item.configDocumentation || item.displayName;
            return {
              id: item.configKey,
              label: item.displayName || item.configKey,
              value: item.configValue || '',
              fieldName: item.configKey,
              alt: item.alt || item.displayName || item.configKey,
              description: documentation
            };
          }).filter((item: ImageField | null) => item !== null) as ImageField[];
          
          // Store image config metadata for save
          imageConfigDataRef.current = (actualResp.configs?.images || [])
            .filter((item: any) => item.configKey) // Only include valid items
            .map((item: any) => {
              // Backend returns configDocumentation, use it if documentation is not available
              // Preserve documentation value as-is from API (including "Null" string if that's what API returns)
              const doc = item.documentation || item.configDocumentation;
              const normalizedDoc = (doc === null || doc === undefined) ? '' : String(doc).trim();
              return {
                configKey: item.configKey,
                configType: item.configType || 'EMAIL_CONFIG',
                configLevel: item.configLevel || 'pilot',
                configDataType: item.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
                displayName: item.displayName || item.configKey,
                documentation: normalizedDoc,
                configRegex: item.configRegex || '.*',
                component: item.component || 'Email'
              };
            });
          
          // Store color config metadata for save
          colorConfigDataRef.current = (actualResp.configs?.colors || [])
            .filter((item: any) => item.configKey && item.shade && item.shade !== '' && item.stdColorDisplayName && item.stdColorDisplayName !== '')
            .map((item: any) => {
              // Handle "Null" string documentation
              const doc = item.documentation || item.configDocumentation;
              const normalizedDoc = (doc === null || doc === undefined || doc === 'Null') ? '' : String(doc).trim();
              // Create uniqueKey for linking to deduplicated color entries
              const uniqueKey = `${item.stdColorId || 'no-std'}_${item.shade}_${(item.hexValue || item.configValue || '#000000').toUpperCase()}`;
              return {
                configKey: item.configKey,
                configType: item.configType || 'EMAIL_CONFIG',
                configLevel: item.configLevel || 'pilot',
                configDataType: item.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
                displayName: item.displayName || item.configKey,
                documentation: normalizedDoc,
                configRegex: item.configRegex || '.*',
                component: item.component || 'Email',
                uniqueKey: uniqueKey 
              };
            });
          
          // Transform metadata from configs
          const configsMetadata = actualResp.configs?.metadata || [];
          const transformedMetadata: MetadataField[] = configsMetadata.map((item: any) => {
            if (!item.configKey) {
              console.warn('[InspectorPanel] Metadata item missing configKey:', item);
              return null;
            }
            
            // Helper function to check if a value is a string boolean
            const isStringBoolean = (val: any): boolean => {
              if (typeof val === 'string') {
                const normalized = val.toLowerCase().trim();
                return normalized === 'true' || normalized === 'false';
              }
              return false;
            };
            
            // Helper function to convert string boolean to actual boolean
            const convertStringBoolean = (val: string): boolean => {
              return val.toLowerCase().trim() === 'true';
            };
            
            // Determine type based on configDataType and value
            let type: 'boolean' | 'enum' | 'number' | 'string' = 'string';
            if (item.configDataType === 'number') {
              type = 'number';
            } else if (item.configDataType === 'boolean' || (item.configValue !== undefined && item.configValue !== null && isStringBoolean(item.configValue))) {
              // Treat as boolean if configDataType is 'boolean' OR if value is string "true"/"false"
              type = 'boolean';
            } else if (item.possibleValues && Array.isArray(item.possibleValues) && item.possibleValues.length > 0) {
              type = 'enum';
            }
            
            // Convert value based on type
            let value: any;
            if (item.configValue !== undefined && item.configValue !== null) {
              if (type === 'boolean') {
                // Convert string booleans to actual booleans
                if (typeof item.configValue === 'string') {
                  value = convertStringBoolean(item.configValue);
                } else {
                  value = Boolean(item.configValue);
                }
              } else if (type === 'number') {
                value = typeof item.configValue === 'number' ? item.configValue : parseFloat(String(item.configValue)) || 0;
              } else {
                value = item.configValue;
              }
            } else {
              // Default value based on type
              value = type === 'boolean' ? false : (type === 'number' ? 0 : '');
            }
            
            // Backend returns configDocumentation, map it to documentation
            const documentation = item.documentation || item.configDocumentation || item.displayName;
            return {
              id: item.configKey,
              label: item.displayName || item.configKey,
              type,
              value,
              fieldName: item.configKey,
              description: documentation,
              options: type === 'enum' && item.possibleValues ? item.possibleValues : undefined
            };
          }).filter((item: MetadataField | null) => item !== null) as MetadataField[];
          
          // Store metadata config metadata for save
          metadataConfigDataRef.current = (actualResp.configs?.metadata || [])
            .filter((item: any) => item.configKey) // Only include valid items
            .map((item: any) => {
              // Backend returns configDocumentation, use it if documentation is not available
              // Preserve documentation value as-is from API (including "Null" string if that's what API returns)
              const doc = item.documentation || item.configDocumentation;
              const normalizedDoc = (doc === null || doc === undefined) ? '' : String(doc).trim();
              return {
                configKey: item.configKey,
                configType: item.configType || 'EMAIL_CONFIG',
                configLevel: item.configLevel || 'pilot',
                configDataType: item.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
                displayName: item.displayName || item.configKey,
                documentation: normalizedDoc,
                configRegex: item.configRegex || '.*',
                component: item.component || 'Email',
                possibleValues: item.possibleValues
              };
            });
          
          console.log('[InspectorPanel] Transformed data:', {
            text: transformedText.length,
            colors: transformedColors.length,
            images: transformedImages.length,
            metadata: transformedMetadata.length
          });
          
          const transformedData: InspectorModel = {
            text: transformedText,
            colors: transformedColors,
            images: transformedImages,
            metadata: transformedMetadata,
            signedOff: false
          };
          
          setData(transformedData);
          
          // Store values and original values
          const v: Record<string, any> = {};
          const originalV: Record<string, any> = {};
          // Store text field keys for counter calculation
          const textKeys = new Set<string>();
          transformedText.forEach((t: TextField) => {
            const key = t.fieldName || t.id;
            v[key] = t.value;
            originalV[key] = t.value; // Store original value
            textKeys.add(key); // Track this as a text field key
          });
          textFieldKeysRef.current = textKeys;
          transformedColors.forEach((c: ColorField) => {
            v[c.fieldName || c.id] = c.value;
            originalV[c.fieldName || c.id] = c.value;
          });
          transformedImages.forEach((img: ImageField) => {
            v[img.fieldName || img.id] = img.value;
            originalV[img.fieldName || img.id] = img.value; // Store original value
          });
          transformedMetadata.forEach((m: MetadataField) => {
            v[m.fieldName || m.id] = m.value;
            originalV[m.fieldName || m.id] = m.value; // Store original value
          });
          
          console.log('[InspectorPanel] Stored original values:', {
            text: transformedText.length,
            colors: transformedColors.length,
            images: transformedImages.length,
            metadata: transformedMetadata.length,
            totalKeys: Object.keys(originalV).length,
            sampleKeys: Object.keys(originalV).slice(0, 5)
          });
          
          valuesRef.current = v;
          originalValuesRef.current = originalV;
          onValuesChange && onValuesChange(v);
          
          // Set initial tab based on available data only on first load
          // Preserve activeTab on subsequent reloads (e.g., after save)
          if (isInitialLoadRef.current) {
            if (transformedText.length > 0) setActiveTab('text');
            else if (transformedColors.length > 0) setActiveTab('colors');
            else if (transformedImages.length > 0) setActiveTab('images');
            else if (transformedMetadata.length > 0) setActiveTab('metadata');
            isInitialLoadRef.current = false; // Mark initial load as complete
          }
        }
      } catch (error: any) {
        console.error('[InspectorPanel] Failed to load inspector configs:', error);
        if (!ignore) {
          // Show user-friendly error message
          const errorMessage = error?.response?.data?.error || error?.message || 'Failed to load inspector data';
          console.error('[InspectorPanel] Error details:', {
            message: errorMessage,
            status: error?.response?.status,
            data: error?.response?.data
          });
          
          // Set empty data on error
          setData({ text: [], colors: [], images: [], metadata: [] });
          
          // Optionally show toast error (commented out to avoid spam)
          // toast.error(`Failed to load inspector: ${errorMessage}`);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [rc, templateKey, elementKey, variant.locale, renderedHtml, selectedPilotId, projectAttributes, getTechnicalSectionName, refreshKey]); // Include refreshKey to trigger API reload after save

  // Reset initial load flag when template/element changes
  React.useEffect(() => {
    isInitialLoadRef.current = true;
  }, [templateKey, elementKey, variant.locale]);

  // Auto-resize textareas
  React.useEffect(() => {
    Object.values(textareaRefs.current).forEach(textarea => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [data.text]);

  // Helper function to normalize documentation value - preserve "Null" string from API, convert null/undefined to empty string
  const normalizeDocumentation = (doc: string | null | undefined): string => {
    // If null or undefined, return empty string
    if (doc === null || doc === undefined) return '';
    // Preserve the string value as-is (including "Null" string if that's what API returns)
    return String(doc).trim();
  };

  // Save handler: saves modified fields using both /v1/cx/save (for configs) and /v1/cx/save-strings (for stringResources)
  const handleSave = React.useCallback(async () => {
    const pilotId = selectedPilotId;
    
    if (!pilotId) {
      toast.error('No pilot selected. Please select a pilot from the sidebar.');
      return;
    }
    
    // Extract notificationType (event name) from templateKey - this will be sent as product_type
    const { productType, notificationType } = parseTemplateKey(templateKey);
    const productTypeValue = notificationType || ''; // e.g., "MONTHLY_SUMMARY" - sent as product_type to backend
    
    // Get fuel_type from Redux, defaulting to 'ELECTRIC' if not available
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    
    // For cx-editor saves, always use 'fuel_type' level (not 'pilot')
    // and always send product_type (event name) and fuel_type
    
    // Find all modified text fields and separate into configs vs stringResources
    const modifiedTextConfigFields = data.text.filter((item: TextField) => {
      const key = item.fieldName || item.id;
      const currentValue = valuesRef.current[key];
      const originalValue = originalValuesRef.current[key];
      const isConfig = configDataRef.current.some(c => c.configKey === key);
      return isConfig && currentValue !== originalValue;
    });
    
    const modifiedStringResourceFields = data.text.filter((item: TextField) => {
      const key = item.fieldName || item.id;
      const currentValue = valuesRef.current[key];
      const originalValue = originalValuesRef.current[key];
      const isStringResource = stringResourceDataRef.current.some(s => s.stringResourceId === key);
      return isStringResource && currentValue !== originalValue;
    });

    const modifiedImageFields = data.images.filter((item: ImageField) => {
      const key = item.fieldName || item.id;
      const currentValue = valuesRef.current[key];
      const originalValue = originalValuesRef.current[key];
      const hasMetadata = imageConfigDataRef.current.some(c => c.configKey === key);
      return hasMetadata && currentValue !== originalValue;
    });

    const modifiedColorFields = data.colors.filter((item: ColorField) => {
      const uniqueKey = item.fieldName || item.id;
      const currentValue = valuesRef.current[uniqueKey];
      const originalValue = originalValuesRef.current[uniqueKey];
      const hasLinkedConfigs = colorGroupsRef.current.has(uniqueKey) && 
        (colorGroupsRef.current.get(uniqueKey)?.length ?? 0) > 0;
      return hasLinkedConfigs && currentValue !== originalValue;
    });

    const modifiedMetadataFields = data.metadata.filter((item: MetadataField) => {
      const key = item.fieldName || item.id;
      const currentValue = valuesRef.current[key];
      const originalValue = originalValuesRef.current[key];
      const hasMetadata = metadataConfigDataRef.current.some(c => c.configKey === key);
      return hasMetadata && currentValue !== originalValue;
    });

    if (
      modifiedTextConfigFields.length === 0 &&
      modifiedStringResourceFields.length === 0 &&
      modifiedImageFields.length === 0 &&
      modifiedColorFields.length === 0 &&
      modifiedMetadataFields.length === 0
    ) {
      toast.info('No changes to save');
      return;
    }

    try {
      const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      
      // Transform all modified fields to save API payload format
      const configs: Array<{
        project_id: string;
        config_key: string;
        config_type: string;
        config_value: string;
        config_level: string;
        config_documentation: string;
        config_regex: string;
        config_data_type: string;
        display_name?: string;
        component?: string;
        editable?: boolean;
        is_required?: boolean;
        possible_values?: any[];
        related_configs?: any[];
        source?: string;
        product_type: string;
        fuel_type: string;
      }> = [];

      // Transform text config fields (not stringResources)
      modifiedTextConfigFields.forEach((item: TextField) => {
        const configMeta = configDataRef.current.find(c => c.configKey === (item.fieldName || item.id));
        const key = item.fieldName || item.id;
        const currentValue = valuesRef.current[key] || '';

        configs.push({
          project_id: pilotId,
          config_key: key,
          config_type: configMeta?.configType || 'EMAIL_CONFIG',
          config_value: String(currentValue),
          config_level: 'fuel_type', // Always use 'fuel_type' level for cx-editor saves
          config_documentation: normalizeDocumentation(configMeta?.documentation),
          config_regex: configMeta?.configRegex || '.*',
          config_data_type: configMeta?.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
          display_name: configMeta?.displayName || item.label,
          component: configMeta?.component,
          editable: true,
          is_required: true,
          product_type: productTypeValue,
          fuel_type: fuelType
        });
      });

      // Transform image fields
      modifiedImageFields.forEach((item: ImageField) => {
        const configMeta = imageConfigDataRef.current.find(c => c.configKey === (item.fieldName || item.id));
        const key = item.fieldName || item.id;
        const currentValue = valuesRef.current[key] || '';

        configs.push({
          project_id: pilotId,
          config_key: key,
          config_type: configMeta?.configType || 'EMAIL_CONFIG',
          config_value: String(currentValue),
          config_level: 'fuel_type', // Always use 'fuel_type' level for cx-editor saves
          config_documentation: normalizeDocumentation(configMeta?.documentation),
          config_regex: configMeta?.configRegex || '.*',
          config_data_type: configMeta?.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
          display_name: configMeta?.displayName || item.label,
          component: configMeta?.component,
          editable: true,
          is_required: true,
          product_type: productTypeValue,
          fuel_type: fuelType
        });
      });

      // Transform color fields
      modifiedColorFields.forEach((item: ColorField) => {
        const uniqueKey = item.fieldName || item.id;
        const linkedConfigKeys = colorGroupsRef.current.get(uniqueKey) || [];
        const currentValue = valuesRef.current[uniqueKey] || '';

        linkedConfigKeys.forEach((configKey) => {
          const configMeta = colorConfigDataRef.current.find(c => c.configKey === configKey);
          
          configs.push({
            project_id: pilotId,
            config_key: configKey, 
            config_type: configMeta?.configType || 'EMAIL_CONFIG',
            config_value: String(currentValue),
            config_level: 'fuel_type', // Always use 'fuel_type' level for cx-editor saves
            config_documentation: normalizeDocumentation(configMeta?.documentation),
            config_regex: configMeta?.configRegex || '.*',
            config_data_type: configMeta?.configDataType ?? 'TEXT', // Preserve exact value from API, fallback to 'TEXT'
            display_name: configMeta?.displayName || item.label,
            component: configMeta?.component,
            editable: true,
            is_required: true,
            product_type: productTypeValue,
            fuel_type: fuelType
          });
        });
      });

      // Transform metadata fields
      modifiedMetadataFields.forEach((item: MetadataField) => {
        const configMeta = metadataConfigDataRef.current.find(c => c.configKey === (item.fieldName || item.id));
        const key = item.fieldName || item.id;
        const currentValue = valuesRef.current[key];

        // Use configDataType from API if available, otherwise determine based on field type
        let configDataType = configMeta?.configDataType;
        if (!configDataType) {
          // Fallback: determine based on field type only if API didn't provide configDataType
          if (item.type === 'boolean') {
            configDataType = 'boolean';
          } else if (item.type === 'number') {
            configDataType = 'number';
          } else if (item.type === 'enum') {
            configDataType = 'enum';
          } else {
            configDataType = 'TEXT'; // Default fallback to 'TEXT' instead of 'string'
          }
        }

        configs.push({
          project_id: pilotId,
          config_key: key,
          config_type: configMeta?.configType || 'EMAIL_CONFIG',
          config_value: String(currentValue),
          config_level: 'fuel_type', // Always use 'fuel_type' level for cx-editor saves
          config_documentation: normalizeDocumentation(configMeta?.documentation),
          config_regex: configMeta?.configRegex || '.*',
          config_data_type: configDataType, // Preserve exact value from API (e.g., "TEXT")
          display_name: configMeta?.displayName || item.label,
          component: configMeta?.component,
          editable: true,
          is_required: true,
          possible_values: configMeta?.possibleValues || item.options,
          product_type: productTypeValue,
          fuel_type: fuelType
        });
      });

      // Prepare stringResources payload
      const strings: Array<{
        project_id: string;
        string_key: string;
        string_value: string;
        locale: string;
        string_level: string;
        product_type: string;
        fuel_type: string;
      }> = [];

      modifiedStringResourceFields.forEach((item: TextField) => {
        const stringMeta = stringResourceDataRef.current.find(s => s.stringResourceId === (item.fieldName || item.id));
        const key = item.fieldName || item.id;
        const currentValue = valuesRef.current[key] || '';

        // For Web channel, use "WEB_DASHBOARD.{productTypeValue}" as product_type for strings payload
        const stringsProductType = productType === 'WEB' ? `WEB_DASHBOARD.${productTypeValue}` : productTypeValue;

        strings.push({
          project_id: pilotId,
          string_key: key,
          string_value: String(currentValue),
          locale: stringMeta?.locale || variant.locale || 'en_US',
          string_level: 'fuel_type', // Always use 'fuel_type' level for cx-editor saves
          product_type: stringsProductType,
          fuel_type: fuelType
        });
      });

      // Call both APIs in parallel if both have changes, otherwise call the one that has changes
      const promises: Promise<any>[] = [];
      let configResult: any = null;
      let stringsResult: any = null;

      // Call /v1/cx/save for configs (text configs, images, colors, metadata)
      if (configs.length > 0) {
        const savePayload = {
          project_id: pilotId,
          configs,
          source_name: 'cx_editor',
          enable_retry: true
        };

        console.log('[InspectorPanel] Calling /v1/cx/save with payload:', savePayload);
        promises.push(
          api.save(savePayload).then((result: any) => {
            configResult = result;
            return result;
          })
        );
      }

      // Call /v1/cx/save-strings for stringResources
      if (strings.length > 0) {
        const saveStringsPayload = {
          project_id: pilotId,
          strings,
          source_name: 'cx_editor'
        };

        console.log('[InspectorPanel] Calling /v1/cx/save-strings with payload:', saveStringsPayload);
        promises.push(
          api.saveStrings(saveStringsPayload).then((result: any) => {
            stringsResult = result;
            return result;
          })
        );
      }

      // Wait for all saves to complete
      const results = await Promise.all(promises);
      
      // Check if any save failed
      const configFailed = configResult && !configResult.success;
      const stringsFailed = stringsResult && !stringsResult.success;

      if (configFailed || stringsFailed) {
        const errors: string[] = [];
        if (configFailed) {
          errors.push(`Configs: ${configResult.error || 'Failed to save configurations'}`);
        }
        if (stringsFailed) {
          errors.push(`Strings: ${stringsResult.error || 'Failed to save string resources'}`);
        }
        toast.error(errors.join('; '));
        return;
      }

      // Update original values after successful save
      const allModifiedFields = [
        ...modifiedTextConfigFields,
        ...modifiedStringResourceFields,
        ...modifiedImageFields,
        ...modifiedColorFields,
        ...modifiedMetadataFields
      ];
      
      allModifiedFields.forEach((item) => {
        const key = item.fieldName || item.id;
        originalValuesRef.current[key] = valuesRef.current[key];
      });

      // Update modified count
      if (onModifiedCountChange) {
        onModifiedCountChange(0);
      }

      // Build success message
      const savedCounts: string[] = [];
      if (configs.length > 0) {
        savedCounts.push(`${configs.length} configuration(s)`);
      }
      if (strings.length > 0) {
        savedCounts.push(`${strings.length} string resource(s)`);
      }
      
      const versionIds: string[] = [];
      if (configResult?.versionId) versionIds.push(`Config: ${configResult.versionId}`);
      if (stringsResult?.versionId) versionIds.push(`Strings: ${stringsResult.versionId}`);
      
      const auditIds: string[] = [];
      if (configResult?.auditId) auditIds.push(`Config: ${configResult.auditId}`);
      if (stringsResult?.auditId) auditIds.push(`Strings: ${stringsResult.auditId}`);

      toast.success(
        `Successfully saved ${savedCounts.join(' and ')}. ` +
        (versionIds.length > 0 ? `Version: ${versionIds.join(', ')}. ` : '') +
        (auditIds.length > 0 ? `Audit: ${auditIds.join(', ')}` : '')
      );

      // Trigger preview refresh after successful save
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error: any) {
      console.error('[InspectorPanel] Save failed:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to save configurations');
    }
  }, [data.text, data.images, data.colors, data.metadata, rc, selectedPilotId, templateKey, projectAttributes, variant.locale, onModifiedCountChange, onSaveSuccess]);

  // Cancel/Reset handler: reverts all changes to original values
  const handleCancel = React.useCallback(() => {
    // Reset all values to original
    valuesRef.current = { ...originalValuesRef.current };
    
    // Reset data state to original values
    const resetData: InspectorModel = {
      text: data.text.map((item: TextField) => {
        const key = item.fieldName || item.id;
        const originalValue = originalValuesRef.current[key];
        return { ...item, value: originalValue !== undefined ? originalValue : item.value };
      }),
      colors: data.colors.map((item: ColorField) => {
        const key = item.fieldName || item.id;
        const originalValue = originalValuesRef.current[key];
        return { ...item, value: originalValue !== undefined ? originalValue : item.value };
      }),
      images: data.images.map((item: ImageField) => {
        const key = item.fieldName || item.id;
        const originalValue = originalValuesRef.current[key];
        return { ...item, value: originalValue !== undefined ? originalValue : item.value };
      }),
      metadata: data.metadata.map((item: MetadataField) => {
        const key = item.fieldName || item.id;
        const originalValue = originalValuesRef.current[key];
        return { ...item, value: originalValue !== undefined ? originalValue : item.value };
      }),
      signedOff: data.signedOff
    };
    
    setData(resetData);
    onValuesChange && onValuesChange(valuesRef.current);
    
    // Update modified count (should be 0 after reset)
    if (onModifiedCountChange) {
      onModifiedCountChange(0);
    }
    
    toast.success('Changes canceled - all local edits have been reset');
  }, [data, onValuesChange, onModifiedCountChange]);

  // Handler for color swatch click - navigates to EditColorPalettePage
  const handleColorSwatchClick = React.useCallback((item: ColorField) => {
    if (!item.fieldName) {
      console.warn('[InspectorPanel] Cannot navigate - missing fieldName');
      return;
    }
    
    const parts = item.fieldName.split('_');
    const stdColorId = parts[0] || '';
    const shade = parts[1] || '';
    
    if (!stdColorId || stdColorId === 'no-std' || !shade) {
      console.warn('[InspectorPanel] Cannot navigate - missing stdColorId or shade', { fieldName: item.fieldName, stdColorId, shade });
      return;
    }
    
    const standardColorId = `${stdColorId}_${shade}`;
    const searchParams = new URLSearchParams({
      hex: item.value || '#FFFFFF',
      source: 'CXtemplateEditor',
      templateKey: templateKey || '',
      elementKey: elementKey || 'Full Template',
      locale: variant.locale || 'en_US',
      scenario: variant.scenario || 'default',
    });
    
    const editPath = ROUTE_TREE.CONTENT_MANAGEMENT.EDIT_COLOR_PALETTE.path.replace(':standardColorId', standardColorId);
    navigate(`${editPath}?${searchParams.toString()}`);
  }, [navigate, templateKey, elementKey, variant.locale, variant.scenario]);

  // Calculate modified count - uses refs directly to avoid dependency issues
  const getModifiedCount = React.useCallback(() => {
    let count = 0;
    
    // Helper to normalize values for comparison (treat null/undefined as empty string)
    const normalizeValue = (val: any): string => {
      if (val === null || val === undefined) return '';
      return String(val);
    };
    
    // Count modified text fields (both configs.text and stringResources.text)
    // Check all text field keys against originalValuesRef
    textFieldKeysRef.current.forEach((key) => {
      // Check if key exists in both refs (was loaded from API and has current value)
      if (key in originalValuesRef.current && key in valuesRef.current) {
        const currentValue = normalizeValue(valuesRef.current[key]);
        const originalValue = normalizeValue(originalValuesRef.current[key]);
        const isModified = currentValue !== originalValue;
        if (isModified) {
          // Count all modified text fields (both configs and stringResources) for better UX
          const isConfig = configDataRef.current.some(c => c.configKey === key);
          console.log('[InspectorPanel] getModifiedCount - text field modified:', key, {
            isConfig,
            current: currentValue,
            original: originalValue
          });
          count++;
        }
      }
    });
    
    // Count modified image fields
    imageConfigDataRef.current.forEach((configMeta) => {
      const key = configMeta.configKey;
      if (key in originalValuesRef.current && key in valuesRef.current) {
        const currentValue = normalizeValue(valuesRef.current[key]);
        const originalValue = normalizeValue(originalValuesRef.current[key]);
        if (currentValue !== originalValue) {
          count++;
        }
      }
    });

    // Count modified color fields - use uniqueKey from colorGroupsRef
    colorGroupsRef.current.forEach((configKeys, uniqueKey) => {
      if (uniqueKey in originalValuesRef.current && uniqueKey in valuesRef.current) {
        const currentValue = normalizeValue(valuesRef.current[uniqueKey]);
        const originalValue = normalizeValue(originalValuesRef.current[uniqueKey]);
        if (currentValue !== originalValue) {
          count++;
        }
      }
    });

    // Count modified metadata fields
    metadataConfigDataRef.current.forEach((configMeta) => {
      const key = configMeta.configKey;
      if (key in originalValuesRef.current && key in valuesRef.current) {
        // Check if this is a boolean metadata field
        const metadataItem = data.metadata.find(m => (m.fieldName || m.id) === key);
        if (metadataItem?.type === 'boolean') {
          // Normalize boolean values (handle string "true"/"false" as well)
          const normalizeBoolean = (val: any): boolean => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') {
              return val.toLowerCase().trim() === 'true';
            }
            return Boolean(val);
          };
          const currentValue = normalizeBoolean(valuesRef.current[key]);
          const originalValue = normalizeBoolean(originalValuesRef.current[key]);
          if (currentValue !== originalValue) {
            count++;
          }
        } else {
          // For non-boolean metadata, use string normalization
          const currentValue = normalizeValue(valuesRef.current[key]);
          const originalValue = normalizeValue(originalValuesRef.current[key]);
          if (currentValue !== originalValue) {
            count++;
          }
        }
      }
    });
    
    console.log('[InspectorPanel] getModifiedCount - total count:', count, {
      textFieldKeys: textFieldKeysRef.current.size,
      textConfigs: configDataRef.current.length,
      imageConfigs: imageConfigDataRef.current.length,
      colorConfigs: colorConfigDataRef.current.length,
      metadataConfigs: metadataConfigDataRef.current.length,
      originalValuesKeys: Object.keys(originalValuesRef.current).length,
      currentValuesKeys: Object.keys(valuesRef.current).length
    });
    
    return count;
  }, []); // No dependencies - reads from refs directly

  // Update modified count when data loads initially (using a ref to track if data has loaded)
  const dataLoadedRef = React.useRef(false);
  React.useEffect(() => {
    const hasData = data.text.length > 0 || data.images.length > 0 || data.colors.length > 0 || data.metadata.length > 0;
    if (hasData && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      // Small delay to ensure refs are updated
      const timer = setTimeout(() => {
        if (onModifiedCountChange) {
          const count = getModifiedCount();
          onModifiedCountChange(count);
        }
      }, 0);
      return () => clearTimeout(timer);
    } else if (!hasData) {
      dataLoadedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.text.length, data.images.length, data.colors.length, data.metadata.length]);

  // Expose save function to parent via onSaveReady callback
  React.useEffect(() => {
    if (onSaveReady) {
      onSaveReady(handleSave);
    }
  }, [onSaveReady, handleSave]);

  // Expose cancel function to parent via onCancelReady callback
  React.useEffect(() => {
    if (onCancelReady) {
      onCancelReady(handleCancel);
    }
  }, [onCancelReady, handleCancel]);

  const handleTextChange = (id: string, value: string) => {
    const next = { ...data, text: data.text.map(x => x.id === id ? { ...x, value } : x) };
    setData(next);
    const item = data.text.find(x => x.id === id);
    const key = item?.fieldName || id;
    const v = { ...valuesRef.current, [key]: value };
    valuesRef.current = v;
    onValuesChange && onValuesChange(v);
    // Update modified count immediately after ref update (refs are synchronous)
    if (onModifiedCountChange) {
      // Debug: check if key exists in refs before counting
      const hasOriginal = key in originalValuesRef.current;
      const hasCurrent = key in valuesRef.current;
      const originalValue = originalValuesRef.current[key];
      const currentValue = valuesRef.current[key];
      const isInConfigData = configDataRef.current.some(c => c.configKey === key);
      console.log('[InspectorPanel] handleTextChange - key:', key, {
        hasOriginal,
        hasCurrent,
        isInConfigData,
        originalValue,
        currentValue,
        valuesMatch: originalValue === currentValue
      });
      const count = getModifiedCount();
      console.log('[InspectorPanel] handleTextChange - final count:', count);
      onModifiedCountChange(count);
    }
  };

  const handleColorChange = (id: string, value: string) => {
    const next = { ...data, colors: data.colors.map(x => x.id === id ? { ...x, value } : x) };
    setData(next);
    const item = data.colors.find(x => x.id === id);
    const key = item?.fieldName || id;
    const v = { ...valuesRef.current, [key]: value };
    valuesRef.current = v;
    onValuesChange && onValuesChange(v);
    // Update modified count immediately after ref update (refs are synchronous)
    if (onModifiedCountChange) {
      const count = getModifiedCount();
      onModifiedCountChange(count);
    }
  };

  const handleImageChange = (id: string, value: string) => {
    const next = { ...data, images: data.images.map(x => x.id === id ? { ...x, value } : x) };
    setData(next);
    const item = data.images.find(x => x.id === id);
    const key = item?.fieldName || id;
    const v = { ...valuesRef.current, [key]: value };
    valuesRef.current = v;
    onValuesChange && onValuesChange(v);
    // Update modified count immediately after ref update (refs are synchronous)
    if (onModifiedCountChange) {
      const count = getModifiedCount();
      onModifiedCountChange(count);
    }
  };

  const handleSignOff = async () => {
    if (isSignedOff) {
      setUndoSignOffOpen(true);
      return;
    }
              try {
                const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      await api.signoff({ templateKey, elementKey, variant, signedOff: true });
      setData({ ...data, signedOff: true });
      toast.success('Signed off');
              } catch {
                toast.error('Signoff failed');
              }
  };

  const handleConfirmUndoSignOff = async () => {
    try {
      const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      await api.signoff({ templateKey, elementKey, variant, signedOff: false });
      setData({ ...data, signedOff: false });
      toast.success('Signoff revoked');
      setUndoSignOffOpen(false);
    } catch {
      toast.error('Signoff revocation failed');
    }
  };

  const handleColorSourceToggle = (color: ColorField) => {
    if (color.isOverride) {
      setOverrideAlertOpen({ open: true, colorId: color.id, action: 'revert' });
    } else {
      setOverrideAlertOpen({ open: true, colorId: color.id, action: 'create' });
    }
  };

  const handleConfirmOverride = async () => {
    const { colorId, action } = overrideAlertOpen;
    if (!colorId) return;
                    try {
                      const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      await api.toggleOverride({ templateKey, elementKey, field: { type: 'color', id: colorId }, action: action as any });
      const next = { ...data, colors: data.colors.map(x => x.id === colorId ? { ...x, isOverride: action === 'create' } : x) };
                      setData(next);
                      toast.success(action === 'create' ? 'Override enabled' : 'Override reverted');
      setOverrideAlertOpen({ open: false });
                    } catch {
                      toast.error('Override toggle failed');
                    }
  };

  const handleCopyImageUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied to clipboard');
  };

  const getOriginalValue = (id: string, type: 'text' | 'color' | 'image' | 'metadata') => {
    // Get original value from originalValuesRef (stored when data loads)
    // Find the item to get its fieldName, then look up in originalValuesRef
    let key: string | undefined;
    if (type === 'text') {
      key = data.text.find(t => t.id === id)?.fieldName || id;
    } else if (type === 'color') {
      key = data.colors.find(c => c.id === id)?.fieldName || id;
    } else if (type === 'image') {
      key = data.images.find(img => img.id === id)?.fieldName || id;
    } else if (type === 'metadata') {
      key = data.metadata.find(m => m.id === id)?.fieldName || id;
    } else {
      key = id;
    }
    return originalValuesRef.current[key];
  };

  const isModified = (id: string, type: 'text' | 'color' | 'image' | 'metadata', currentValue: any) => {
    const original = getOriginalValue(id, type);
    
    // For metadata, check if it's a boolean field
    if (type === 'metadata') {
      const metadataItem = data.metadata.find(m => m.id === id);
      if (metadataItem?.type === 'boolean') {
        // Normalize boolean values (handle string "true"/"false" as well)
        const normalizeBoolean = (val: any): boolean => {
          if (val === null || val === undefined) return false;
          if (typeof val === 'string') {
            return val.toLowerCase().trim() === 'true';
          }
          return Boolean(val);
        };
        return normalizeBoolean(original) !== normalizeBoolean(currentValue);
      }
    }
    
    // Normalize values for comparison (treat null/undefined as empty string)
    const normalizeValue = (val: any): string => {
      if (val === null || val === undefined) return '';
      return String(val);
    };
    return normalizeValue(original) !== normalizeValue(currentValue);
  };

  if (!isOpen) {
    return (
      <Box sx={{ width: 48, height: '100%', display: 'flex', alignItems: 'flex-start', pt: 2 }}>
        <IconButton onClick={onToggleOpen} size="small">
          <ChevronLeftIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'hidden' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 500 }}>Inspector</Typography>
            <Tooltip title="Open Color Palette">
              <IconButton 
                size="small" 
                href="/content-management/color-palette"
                sx={{ 
                  width: 28,
                  height: 28,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        action={
          <Tooltip title="Collapse Inspector">
            <IconButton 
              onClick={onToggleOpen} 
              size="small"
              sx={{ 
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        }
        sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}
      />
      
      {!templateKey ? (
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Select a template to start editing.
          </Typography>
        </CardContent>
      ) : loading ? (
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      ) : (
        <>
          <Box sx={{ px: 2, pt: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5, textAlign: 'left' }}>
                  {isFullTemplate ? 'Template:' : 'Element:'}
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.875rem', wordBreak: 'break-word', textAlign: 'left' }}>
                  {getDisplayName(elementKey)}
                </Typography>
              </Box>
              <Tooltip title="Sign off functionality is not available in MVP">
                <span>
                <Button
                  variant={isSignedOff ? 'contained' : 'outlined'}
                  size="small"
                  onClick={handleSignOff}
                    disabled={true}
                  startIcon={isSignedOff ? <CheckCircleIcon /> : <EditIcon />}
                  sx={{
                    fontSize: '0.875rem',
                    flexShrink: 0,
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    ...(isSignedOff && {
                      bgcolor: 'success.light',
                      color: 'success.dark',
                      borderColor: 'success.main',
                      '&:hover': { 
                          bgcolor: 'success.light',
                          color: 'success.dark'
                      }
                    })
                  }}
                >
                  {isSignedOff ? 'Signed Off' : 'Sign Off'}
                </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  p: 0.5,
                  gap: 0.5,
                  bgcolor: 'background.paper',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                {data.text.length > 0 && (
                  <Tooltip title="Text">
                    <Button
                      onClick={() => setActiveTab('text')}
                      sx={{
                        minHeight: 40,
                        minWidth: 60,
                        px: 2,
                        py: 1,
                        gap: 1,
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        flex: 1,
                        ...(activeTab === 'text' && {
                          bgcolor: 'action.selected',
                          color: 'text.primary'
                        }),
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <FileTextIcon sx={{ fontSize: 20 }} />
                      {data.text.length}
                    </Button>
                  </Tooltip>
                )}
                {data.images.length > 0 && (
                  <Tooltip title="Images">
                    <Button
                      onClick={() => setActiveTab('images')}
                      sx={{
                        minHeight: 40,
                        minWidth: 60,
                        px: 2,
                        py: 1,
                        gap: 1,
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        flex: 1,
                        ...(activeTab === 'images' && {
                          bgcolor: 'action.selected',
                          color: 'text.primary'
                        }),
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 20 }} />
                      {data.images.length}
                    </Button>
                  </Tooltip>
                )}
                {data.colors.length > 0 && (
                  <Tooltip title="Colors">
                    <Button
                      onClick={() => setActiveTab('colors')}
                      sx={{
                        minHeight: 40,
                        minWidth: 60,
                        px: 2,
                        py: 1,
                        gap: 1,
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        flex: 1,
                        ...(activeTab === 'colors' && {
                          bgcolor: 'action.selected',
                          color: 'text.primary'
                        }),
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <PaletteIcon sx={{ fontSize: 20 }} />
                      {data.colors.length}
                    </Button>
                  </Tooltip>
                )}
                {data.metadata.length > 0 && (
                  <Tooltip title="Metadata">
                    <Button
                      onClick={() => setActiveTab('metadata')}
                      sx={{
                        minHeight: 40,
                        minWidth: 60,
                        px: 2,
                        py: 1,
                        gap: 1,
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        flex: 1,
                        ...(activeTab === 'metadata' && {
                          bgcolor: 'action.selected',
                          color: 'text.primary'
                        }),
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 20 }} />
                      {data.metadata.length}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, minWidth: 0, maxWidth: '100%' }}>
              {activeTab === 'text' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                  {data.text.map((item: TextField) => {
                    const modified = isModified(item.id, 'text', item.value);
                    // Check if this is a config or stringResource
                    const configItem = configDataRef.current.find(c => c.configKey === item.id);
                    const stringResourceItem = stringResourceDataRef.current.find(s => s.stringResourceId === item.id);
                    const isConfig = !!configItem;
                    const isStringResource = !!stringResourceItem;
                    
                    // Determine tooltip content
                    let tooltipDescription = '';
                    let tooltipParameter = '';
                    
                    if (isConfig) {
                      // For configs: show configDocumentation
                      tooltipDescription = configItem.documentation || '';
                      tooltipParameter = item.fieldName || item.id;
                    } else if (isStringResource) {
                      // For stringResources: show stringResourceId
                      tooltipDescription = stringResourceItem.documentation || '';
                      tooltipParameter = stringResourceItem.stringResourceId || item.id;
                    } else {
                      // Fallback to original behavior
                      tooltipDescription = item.description || '';
                      tooltipParameter = item.fieldName || item.id;
                    }
                    
                    return (
                      <Box key={item.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.75rem',
                              ...(isSignedOff && { cursor: 'not-allowed' })
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                {tooltipDescription && (
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    {tooltipDescription}
                                  </Typography>
                                )}
                                {tooltipParameter && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block',
                                      fontFamily: 'monospace',
                                      bgcolor: 'rgba(0,0,0,0.05)',
                                      p: 0.5,
                                      borderRadius: 0.5
                                    }}
                                  >
                                    Parameter: {tooltipParameter}
                                  </Typography>
                                )}
                              </Box>
                            }
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  boxShadow: 2,
                                  maxWidth: 320,
                                  fontSize: '0.75rem',
                                  p: 1.5
                                }
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{ 
                                p: 0.25,
                                color: 'rgba(0, 0, 0, 0.38)',
                                flexShrink: 0,
                                '&:hover': { 
                                  bgcolor: 'transparent',
                                  color: 'rgba(0, 0, 0, 0.54)'
                                }
                              }}
                            >
                              <InfoIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <MuiTextField
                          fullWidth
                          multiline
                          value={item.value}
                          onChange={(e) => handleTextChange(item.id, e.target.value)}
                          disabled={isSignedOff}
                          size="small"
                          minRows={1}
                          maxRows={4}
                          inputRef={(el) => {
                            if (el) textareaRefs.current[item.id] = el;
                          }}
                          sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              minHeight: '20px !important',
                              ...(isSignedOff && { 
                                bgcolor: 'action.disabledBackground',
                                cursor: 'not-allowed'
                              }),
                              ...(modified && {
                                bgcolor: 'warning.light',
                                '&:hover': {
                                  bgcolor: 'warning.light'
                                }
                              })
                            },
                            '& .MuiInputBase-input': {
                              minHeight: '20px !important',
                              py: '2px !important',
                              lineHeight: '1.2 !important'
                            },
                            ...(modified && {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important',
                                borderWidth: '2px !important'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important'
                              },
                              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important',
                                borderWidth: '2px !important'
                              }
                            })
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}

              {activeTab === 'colors' && (
                <Box sx={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }}>
                  <Table size="small" sx={{ minWidth: 400, width: '100%', tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 120, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Label</TableCell>
                        <TableCell sx={{ minWidth: 140, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Color (Hex)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 90, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Contrast</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.colors.map((item: ColorField) => {
                        const modified = isModified(item.id, 'color', item.value);
                        const isInherited = !item.isOverride;
                        // Use pre-calculated contrast values from API
                        const contrastRatio = (item as any).contrastRatio;
                        const contrastPass = (item as any).contrastPass;
                        const hasContrastData = contrastRatio !== null && contrastRatio !== undefined;

                        return (
                          <TableRow
                            key={item.id}
                            sx={{
                              fontSize: '0.75rem',
                              ...(modified && { 
                                bgcolor: 'warning.light',
                                '&:hover': {
                                  bgcolor: 'warning.light'
                                }
                              })
                            }}
                          >
                            <TableCell sx={{ minWidth: 120 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                  {item.label}
                                </Typography>
                                {item.description && (
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                          {item.description}
                                        </Typography>
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'background.paper',
                                          color: 'text.primary',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          boxShadow: 2,
                                          maxWidth: 320,
                                          fontSize: '0.75rem',
                                          p: 1.5
                                        }
                                      }
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ 
                                        p: 0.25,
                                        color: 'rgba(0, 0, 0, 0.38)',
                                        flexShrink: 0,
                                        '&:hover': { 
                                          bgcolor: 'transparent',
                                          color: 'rgba(0, 0, 0, 0.54)'
                                        }
                                      }}
                                    >
                                      <InfoIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Click to edit color in Color Palette" placement="top">
                                  <Box
                                    component="button"
                                    type="button"
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                      bgcolor: item.value,
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                      p: 0,
                                      transition: 'transform 0.2s, box-shadow 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: 2,
                                      }
                                    }}
                                    onClick={() => handleColorSwatchClick(item)}
                                  />
                                </Tooltip>
                                <MuiTextField
                                  size="small"
                                  value={item.value}
                                  InputProps={{
                                    readOnly: true  // Always view-only, navigation via color swatch only
                                  }}
                                  sx={{
                                    width: 80,
                                    flexShrink: 0,
                                    '& .MuiInputBase-root': {
                                      height: 'auto',
                                      bgcolor: 'background.paper',
                                      cursor: 'default'
                                    },
                                    '& .MuiInputBase-input': {
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      py: 0.5,
                                      cursor: 'default'
                                    }
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: 90 }}>
                              {hasContrastData ? (
                                <Chip
                                  size="small"
                                  icon={contrastPass ? <CheckIcon sx={{ fontSize: 16 }} /> : <CloseIcon sx={{ fontSize: 16 }} />}
                                  label={contrastRatio.toFixed(2)}
                                  sx={{ 
                                    bgcolor: contrastPass 
                                      ? '#E8F5E9' 
                                      : '#FFE5E5',
                                    color: contrastPass 
                                      ? '#2E7D32' 
                                      : '#8B0000',
                                    border: contrastPass 
                                      ? '1px solid #A5D6A7' 
                                      : '1px solid #FFB3B3',
                                    borderRadius: '16px',
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    '& .MuiChip-label': {
                                      px: 1.5,
                                    },
                                    '& .MuiChip-icon': {
                                      color: contrastPass 
                                        ? '#2E7D32' 
                                        : '#8B0000',
                                    },
                                  }}
                                />
                              ) : (
                                <Chip 
                                  label="N/A" 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ fontSize: '0.75rem', height: 24, borderRadius: '16px' }} 
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {activeTab === 'images' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {data.images.map((item: ImageField) => {
                    const modified = isModified(item.id, 'image', item.value);
                    return (
                      <Box key={item.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Tooltip title={item.fieldName || item.id}>
                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                            {item.label}
                          </Typography>
                          </Tooltip>
                            <Tooltip title="Copy URL">
                              <IconButton
                                size="small"
                              onClick={() => handleCopyImageUrl(item.value)}
                              sx={{ ml: 1 }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        <MuiTextField
                          fullWidth
                          value={item.value}
                          onChange={(e) => handleImageChange(item.id, e.target.value)}
                          disabled={isSignedOff}
                          size="small"
                          placeholder="Enter image URL"
                          sx={{
                            mt: 0.5,
                            '& .MuiInputBase-root': {
                              ...(isSignedOff && { bgcolor: 'action.disabledBackground' })
                            },
                            ...(modified && {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important',
                                borderWidth: '2px !important'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important'
                              },
                              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'warning.main !important',
                                borderWidth: '2px !important'
                              }
                            })
                          }}
                        />
                        {item.value && (
                          <Box sx={{ mt: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                          <img
                            src={item.value}
                            alt={item.alt || item.label}
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }}
                              onError={(e) => {
                                // Hide broken image or show placeholder
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                          />
                        </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {activeTab === 'metadata' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {data.metadata.map((item: MetadataField) => {
                    const modified = isModified(item.id, 'metadata', item.value);
                    return (
                      <Box key={item.id} sx={{ width: '100%', minWidth: 0 }}>
                        {item.type === 'boolean' && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1.5,
                              border: 1,
                              borderColor: modified ? 'warning.main' : 'divider',
                              borderRadius: 1,
                              transition: 'all 0.2s ease-in-out',
                              bgcolor: modified 
                                ? 'warning.light' 
                                : isSignedOff 
                                  ? 'action.disabledBackground' 
                                  : 'transparent',
                              '&:hover': {
                                ...(!isSignedOff && !modified && {
                                  bgcolor: 'action.hover'
                                })
                              }
                            }}
                          >
                            <Box 
                              component="label"
                              htmlFor={`metadata-${item.id}`}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5,
                                flex: '1 1 auto',
                                minWidth: 0,
                                maxWidth: 'calc(100% - 60px)', // Reserve space for switch (48px) + margin (12px)
                                mr: 2,
                                cursor: isSignedOff ? 'not-allowed' : 'pointer',
                                overflow: 'hidden',
                                '&:hover': {
                                  ...(!isSignedOff && {
                                    opacity: 0.8
                                  })
                                }
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.875rem',
                                  fontWeight: 400,
                                  color: 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: '1 1 auto',
                                  minWidth: 0,
                                  textAlign: 'left',
                                  ...(isSignedOff && { color: 'text.disabled' })
                                }}
                              >
                                {item.label}
                              </Typography>
                              {item.description && (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        {item.description}
                                      </Typography>
                                    </Box>
                                  }
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 2,
                                        maxWidth: 320,
                                        fontSize: '0.75rem',
                                        p: 1.5
                                      }
                                    }
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ 
                                      p: 0,
                                      width: 14,
                                      height: 14,
                                      minWidth: 14,
                                      flexShrink: 0,
                                      color: 'rgba(0, 0, 0, 0.38)',
                                      '&:hover': { 
                                        bgcolor: 'transparent',
                                        color: 'rgba(0, 0, 0, 0.54)'
                                      }
                                    }}
                                  >
                                    <InfoIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Switch
                              id={`metadata-${item.id}`}
                              checked={(() => {
                                // Ensure value is always a boolean, even if it comes as string "true"/"false"
                                const val = item.value;
                                if (typeof val === 'string') {
                                  return val.toLowerCase().trim() === 'true';
                                }
                                return Boolean(val);
                              })()}
                              onChange={(e) => {
                                const next = { ...data, metadata: data.metadata.map(x => x.id === item.id ? { ...x, value: e.target.checked } : x) };
                                setData(next);
                                const key = item.fieldName || item.id;
                                const v = { ...valuesRef.current, [key]: e.target.checked };
                                valuesRef.current = v;
                                onValuesChange && onValuesChange(v);
                                // Update modified count immediately after ref update (refs are synchronous)
                                if (onModifiedCountChange) {
                                  const count = getModifiedCount();
                                  onModifiedCountChange(count);
                                }
                              }}
                              disabled={isSignedOff}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                '& .MuiSwitch-switchBase': {
                                  padding: '4px'
                                }
                              }}
                            />
                          </Box>
                        )}
                        {item.type === 'enum' && (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  ...(isSignedOff && { cursor: 'not-allowed' })
                                }}
                              >
                                {item.label}
                              </Typography>
                              {item.description && (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        {item.description}
                                      </Typography>
                                    </Box>
                                  }
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 2,
                                        maxWidth: 320,
                                        fontSize: '0.75rem',
                                        p: 1.5
                                      }
                                    }
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{ 
                                      p: 0.25,
                                      color: 'rgba(0, 0, 0, 0.38)',
                                      flexShrink: 0,
                                      '&:hover': { 
                                        bgcolor: 'transparent',
                                        color: 'rgba(0, 0, 0, 0.54)'
                                      }
                                    }}
                                  >
                                    <InfoIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Select
                              fullWidth
                              value={item.value as string}
                              onChange={(e) => {
                                const next = { ...data, metadata: data.metadata.map(x => x.id === item.id ? { ...x, value: e.target.value } : x) };
                                setData(next);
                                const key = item.fieldName || item.id;
                                const v = { ...valuesRef.current, [key]: e.target.value };
                                valuesRef.current = v;
                                onValuesChange && onValuesChange(v);
                                // Update modified count immediately after ref update (refs are synchronous)
                                if (onModifiedCountChange) {
                                  const count = getModifiedCount();
                                  onModifiedCountChange(count);
                                }
                              }}
                              disabled={isSignedOff}
                              size="small"
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  ...(isSignedOff && { bgcolor: 'action.disabledBackground' })
                                },
                                ...(modified && {
                                  bgcolor: 'warning.light',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important',
                                    borderWidth: '2px !important'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important'
                                  },
                                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important',
                                    borderWidth: '2px !important'
                                  }
                                })
                              }}
                            >
                              {item.options?.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </>
                        )}
                        {(item.type === 'string' || item.type === 'number') && (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  ...(isSignedOff && { cursor: 'not-allowed' })
                                }}
                              >
                                {item.label}
                              </Typography>
                              {item.description && (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        {item.description}
                                      </Typography>
                                    </Box>
                                  }
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 2,
                                        maxWidth: 320,
                                        fontSize: '0.75rem',
                                        p: 1.5
                                      }
                                    }
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{ 
                                      p: 0.25,
                                      color: 'rgba(0, 0, 0, 0.38)',
                                      flexShrink: 0,
                                      '&:hover': { 
                                        bgcolor: 'transparent',
                                        color: 'rgba(0, 0, 0, 0.54)'
                                      }
                                    }}
                                  >
                                    <InfoIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <MuiTextField
                              fullWidth
                              type={item.type === 'number' ? 'number' : 'text'}
                              value={item.value as string | number}
                              onChange={(e) => {
                                const value = item.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                                const next = { ...data, metadata: data.metadata.map(x => x.id === item.id ? { ...x, value } : x) };
                                setData(next);
                                const key = item.fieldName || item.id;
                                const v = { ...valuesRef.current, [key]: value };
                                valuesRef.current = v;
                                onValuesChange && onValuesChange(v);
                                // Update modified count immediately after ref update (refs are synchronous)
                                if (onModifiedCountChange) {
                                  const count = getModifiedCount();
                                  onModifiedCountChange(count);
                                }
                              }}
                              disabled={isSignedOff}
                              size="small"
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  ...(isSignedOff && { bgcolor: 'action.disabledBackground' }),
                                  ...(modified && {
                                    bgcolor: 'warning.light',
                                    '&:hover': {
                                      bgcolor: 'warning.light'
                                    }
                                  })
                                },
                                ...(modified && {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important',
                                    borderWidth: '2px !important'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important'
                                  },
                                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'warning.main !important',
                                    borderWidth: '2px !important'
                                  }
                                })
                              }}
                            />
                          </>
                        )}
                        {!['boolean', 'enum', 'string', 'number'].includes(item.type) && (
                          <Box
                            sx={{
                              mt: 0.5,
                              p: 1.5,
                              border: 1,
                              borderColor: modified ? 'warning.main !important' : 'divider',
                              borderWidth: modified ? '2px !important' : 1,
                              borderRadius: 1,
                              ...(isSignedOff && { bgcolor: 'action.disabledBackground' })
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {String(item.value ?? '')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Divider />
            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Auto-saved locally
              </Typography>
              <Button variant="outlined" size="small" startIcon={<HistoryIcon />}>
                History
              </Button>
            </Box> */}
          </Box>
        </>
      )}

      {/* Dialogs */}
          <PalettePickerDialog
            open={paletteOpen.open}
            onClose={() => setPaletteOpen({ open: false })}
            onSelect={(tok) => {
              const cid = paletteOpen.colorId;
              if (!cid) return;
          handleColorChange(cid, tok.hex);
          setPaletteOpen({ open: false });
        }}
      />

      {/* Undo Sign Off Dialog */}
      <Dialog open={undoSignOffOpen} onClose={() => setUndoSignOffOpen(false)}>
        <DialogTitle>Undo Sign Off?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoking the sign-off will make this element editable again. This action will be logged in the audit trail.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoSignOffOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmUndoSignOff}>Confirm & Revoke</Button>
        </DialogActions>
      </Dialog>

      {/* Override Alert Dialog */}
      <Dialog open={overrideAlertOpen.open} onClose={() => setOverrideAlertOpen({ open: false })}>
        <DialogTitle>
          {overrideAlertOpen.action === 'create' ? 'Override Inherited Color?' : 'Revert to Inherited Value?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {overrideAlertOpen.action === 'create'
              ? 'This will create a local override, allowing you to set a different color for this element only.'
              : 'This will discard the local override and restore its value from the main Color Palette.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideAlertOpen({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmOverride}>
            {overrideAlertOpen.action === 'create' ? 'Override' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
